import { useHistory, useParams, useLocation } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import DetailInfo from "./Premium/DetailInfo";
import LocationInfo from "./LocationInfo";
import ReportInfo from "./ReportInfo";
import { isPremiumAndVIP, shareURL } from "../../../utils/common";
import LocationSheet from "../../../components/web/LocationSheet";
import { enqueueSnackbar } from "notistack";
import useGeoLocation from "../../../hooks/useGeoLocation";
import useQuickButtons from "../../../hooks/useQuickButtons";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { navigationTargetState } from "../../../recoil/store";
import Loading from "../../../components/Loading";
import ImageViewer from "../../../components/ImageViewer";
import Review from "./Review";
import ReviewWrite from "./WriteReview";
import Header from "./Header";
import ThumbnailSection from "./ThumbnailSection";
import StoreInfo from "./StoreInfo";
import ExtraInfo from "./ExtraInfo";
import InfoMenu from "./InfoMenu";
import { StoreDetailWrapper, HorizontalBar } from "./StoreDetailStyles";
import { StoreContext } from "./StoreContext";
import { useStoreDetail } from "../../../hooks/store/useStoreDetail";
import PremiumThumbnailSection from "./Premium/PremiumThumbnailSection";
import PremiumInfoMenu, { InfoMenuType } from "./Premium/PremiumInfoMenu";
import PremiumExtraInfo from "./Premium/PremiumExtraInfo";
import PubIntro from "./Premium/PubIntro";
import HomeInfo from "./Premium/HomeInfo";
import FloatingMenu from "./Premium/FloatingMenu";
import PremiumLocationInfo from "./Premium/PremiumLocationInfo";
import PremiumReview from "./Premium/PremiumReview";
import { LikeType } from "../../../api/like";
import PubEvent from "./Premium/PubEvent";
import ReservationCancelPopup from "../MyReservation/component/isCancel";
import { cancelCompanyReservation } from "../../../api/company-reservation";
import { reservationCancel } from "../../../api/reservation";
import { CurrentReservation } from "../../../api/types";
import { insetState } from "../../../recoil/inset";
import PubRestaurant from "./Premium/Restaurant";
import { isApp } from "../../../hooks/useNativeApp";
import useScreenOrientation from "../../../hooks/useScreenOrientation";
import OpenApp from "../../../components/openApp";
import YoutubeLive from "./Premium/youtube-live";
import { PubProvider } from "../PremiumPub/hooks/pub-context";
import PremiumPub from "../PremiumPub";

const StoreDetail: React.FC = () => {
  const history = useHistory();
  const { id } = useParams<{ id: string }>();
  const { search } = useLocation();
  const type = new URLSearchParams(search).get("type");
  const {
    data,
    rate,
    showEdit,
    setShowEdit,
    showWrite,
    setShowWrite,
    editReviewData,
    setEditReviewData,
    handleUpdate,
    currentTime,
    liveList,
  } = useStoreDetail(id);
  const [innerRef, setInnerRef] = useState<Element | null>(null);
  const [infoMenu, setInfoMenu] = useState<InfoMenuType>("location");
  const [selectedImageIndex, setSelectedImageIndex] = useState<number>(-1);
  const menuListRef = Array.from({ length: 5 }).map(() =>
    useRef<HTMLDivElement>(null)
  );
  const [showHeader, setShowHeader] = useState(true);
  const setNavigationTarget = useSetRecoilState(navigationTargetState);
  const { latitude, longitude } = useGeoLocation(true);
  const { openPhoneCall, toggleLike } = useQuickButtons();
  const [isReservationCancel, setIsReservationCancel] = useState<boolean>();
  const [reservationCancelItem, setIsReservationCancelItem] =
    useState<CurrentReservation>();
  const orientation = useScreenOrientation();
  const inset = useRecoilValue(insetState);
  const [optimisticLikes, setOptimisticLikes] = useState<
    Record<number, boolean>
  >({});

  const [isOpenUseApp, setIsOpenUseApp] = useState(false);

  // infoMenu 초기값을 data가 로드된 후에 업데이트
  useEffect(() => {
    if (data && infoMenu === "location") {
      setInfoMenu(isPremiumAndVIP(data.pubType) ? "detail" : "location");
    }
  }, [data]);

  useEffect(() => {
    // URL에서 store_redirect 파라미터 확인
    const params = new URLSearchParams(window.location.search);
    const shouldRedirect = params.get("store_redirect");

    if (shouldRedirect === "true" && !isApp) {
      const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
      if (isIOS) {
        window.location.href = "https://apps.apple.com/app/6469495456";
      } else {
        window.location.href =
          "https://play.google.com/store/apps/details?id=com.runnersoft.runnerrunner";
      }
    }
  }, []);

  const goToApp = () => {
    if (!isApp) {
      const isKakao = /KAKAOTALK/i.test(navigator.userAgent);
      const redirectUrl =
        window.location.href +
        (window.location.search ? "&" : "?") +
        "store_redirect=true";
      // 앱 실행 시도
      if (isKakao) {
        enqueueSnackbar(redirectUrl);
        window.location.href =
          "kakaotalk://web/openExternal?url=" + encodeURIComponent(redirectUrl);
      } else {
        const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
        if (isIOS) {
          const link = document.createElement("a");
          link.href = redirectUrl;
          link.style.display = "none";
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        } else {
          window.open(redirectUrl, "_blank");
          window.close();
        }
      }
      return;
    }
  };

  useEffect(() => {
    if (!isApp && orientation !== "landscape") {
      setIsOpenUseApp(true);
    }
  }, []);

  const handleClose = () => {
    console.log(history);
    history.goBack();
  };

  const handleOpenCall = () => {
    if (!data) return;
    if (data.cafeName && data.vcn) {
      openPhoneCall(data.cafeName, data.vcn);
    } else {
      enqueueSnackbar("전화번호가 없습니다.", { variant: "error" });
    }
  };

  const handleClickMenu = (menu: InfoMenuType) => {
    setInfoMenu(menu);
  };

  useEffect(() => {
    if (!innerRef) return;
    const checkScroll = () => {
      if (innerRef.scrollTop > 300 || data.pubType === "NORMAL") {
        // 100px 스크롤되면 header 표시
        setShowHeader(true);
      } else {
        setShowHeader(false);
      }
    };
    checkScroll();
    if (!data) return;

    innerRef.addEventListener("scroll", checkScroll);

    return () => {
      if (innerRef) {
        innerRef.removeEventListener("scroll", checkScroll);
      }
    };
  }, [innerRef, data]);

  const handleCancel = async () => {
    if (!reservationCancelItem) return;
    try {
      if (data.pubType === "RUNNER") {
        const startDate = new Date(reservationCancelItem.tournament.startDate);
        const currentDate = new Date();
        currentDate.setHours(0, 0, 0, 0);

        const startDateOnly = new Date(startDate);
        startDateOnly.setHours(0, 0, 0, 0);

        const dDayRemaining = Math.ceil(
          (startDateOnly.getTime() - currentDate.getTime()) /
            (1000 * 60 * 60 * 24)
        );
        if (dDayRemaining <= 3) {
          enqueueSnackbar("대회 시작 3일 전에는 예약을 취소할 수 없습니다. ", {
            variant: "error",
          });
          setIsReservationCancel(false);
          return;
        }
        await cancelCompanyReservation(reservationCancelItem.id);
      } else {
        await reservationCancel(reservationCancelItem.id);
      }
      enqueueSnackbar("해당 토너먼트 예약 취소되었습니다. ", {
        variant: "success",
      });
      setIsReservationCancel(false);
      handleUpdate();
    } catch (e: any) {
      enqueueSnackbar(e.message, {
        variant: "error",
      });
      setIsReservationCancel(false);
      console.error(e);
    }
  };

  const handleOptimisticToggleLike = async (
    id: number,
    likeType: LikeType
  ): Promise<boolean> => {
    // Immediately update the UI
    const currentLikeState = optimisticLikes[id] ?? data?.like ?? false;
    const newLikeState = !currentLikeState;

    setOptimisticLikes((prev) => ({
      ...prev,
      [id]: newLikeState,
    }));

    try {
      // Make the actual API call
      const result = await toggleLike(id, likeType);

      // Ensure result is boolean
      const booleanResult = Boolean(result);

      // Update with the real result from the server
      setOptimisticLikes((prev) => ({
        ...prev,
        [id]: booleanResult,
      }));

      if (data) {
        data.like = booleanResult;
      }
      return booleanResult;
    } catch (error) {
      // If the API call fails, revert the optimistic update
      setOptimisticLikes((prev) => ({
        ...prev,
        [id]: currentLikeState,
      }));

      if (data) {
        data.like = currentLikeState;
      }

      enqueueSnackbar("좋아요 업데이트에 실패했습니다.", { variant: "error" });
      return currentLikeState;
    }
  };

  if (!data) {
    return <Loading full />;
  }

  const getCurrentLikeState = (id: number) => {
    return optimisticLikes[id] ?? data.like ?? false;
  };

  if (type === "PREMIUM")
    return (
      <PubProvider>
        <PremiumPub />
      </PubProvider>
    );

  return (
    <StoreContext.Provider value={{ data, update: handleUpdate }}>
      {showWrite && (
        <ReviewWrite
          mode="write"
          onClose={() => {
            handleUpdate();
            setShowWrite(false);
          }}
          rate={5}
          pubId={data.id}
        />
      )}
      {showEdit && (
        <ReviewWrite
          mode="edit"
          onClose={() => {
            handleUpdate();
            setShowEdit(false);
          }}
          rate={editReviewData ? editReviewData.score : 5}
          reviewData={editReviewData || undefined}
          pubId={data.id}
        />
      )}

      {selectedImageIndex !== -1 && (
        <ImageViewer
          images={data.images.map((x) => x.imageUrl)}
          defaultIndex={selectedImageIndex}
          onClose={() => setSelectedImageIndex(-1)}
          canEdit={data.myCafe}
        />
      )}

      {isReservationCancel && (
        <ReservationCancelPopup
          onClose={() => setIsReservationCancel(false)}
          handleCancel={handleCancel}
        />
      )}
      <LocationSheet />
      <StoreDetailWrapper ref={setInnerRef} scrollLock={false} isApp={inset}>
        <Header
          show={showHeader}
          onClose={handleClose}
          title={data.cafeName}
          data={{
            ...data,
            like: getCurrentLikeState(data.id), // Use the optimistic state
          }}
          onToggleLike={handleOptimisticToggleLike}
        />
        {isPremiumAndVIP(data.pubType) && (
          <>
            <PremiumThumbnailSection
              rate={rate}
              data={data}
              onImageClick={setSelectedImageIndex}
            />
            <PremiumExtraInfo
              data={data}
              currentTime={currentTime}
              setIsReservationCancelItem={setIsReservationCancelItem}
              setIsReservationCancel={setIsReservationCancel}
            />
            <PremiumInfoMenu
              showRestaurantTab={false}
              currentMenu={infoMenu}
              onMenuClick={handleClickMenu}
              reviewCount={data.reviewData.length}
            />
            {infoMenu === "detail" && (
              <>
                <DetailInfo boxRef={menuListRef[1]} />
                <HomeInfo boxRef={menuListRef[0]} />
                <PubIntro
                  name={data.cafeName}
                  icon={"/image-web/search/Icon/pub_icon.svg"}
                  description={`안녕하세요 ${data.cafeName} 입니다!`}
                />
                <ReportInfo />
              </>
            )}
            {infoMenu === "event" && <PubEvent />}
            {infoMenu === "location" && (
              <PremiumLocationInfo boxRef={menuListRef[3]} />
            )}
            {infoMenu === "review" && (
              <PremiumReview
                boxRef={menuListRef[4]}
                setEditReviewData={setEditReviewData}
                setEditReview={setShowEdit}
                openWrite={() => {
                  setShowWrite(true);
                }}
                closeWrite={() => {
                  setShowWrite(false);
                }}
                reviewData={data.reviewData}
                pubId={data.id}
                todayReviewPhotoAdd={data.todayReviewPhotoAdd}
              />
            )}
            {infoMenu === "restaurant" && (
              <PubRestaurant data={data.restaurant} boxRef={menuListRef[5]} />
            )}
            {liveList.length > 0 && <YoutubeLive list={liveList} />}
            <FloatingMenu
              data={{
                ...data,
                like: getCurrentLikeState(data.id), // Use the optimistic state
              }}
              onOpenCall={handleOpenCall}
              onShare={() => shareURL(window.location.href)}
              onNavigate={() => setNavigationTarget(data)}
              onRestaurnt={() => setInfoMenu("restaurant")}
            />
          </>
        )}

        {!isPremiumAndVIP(data.pubType) && (
          <div
            style={{
              marginTop: inset ? `${inset}px` : 0,
            }}
          >
            <ThumbnailSection
              images={data.images}
              onImageClick={setSelectedImageIndex}
            />
            <StoreInfo
              data={data}
              rate={rate}
              latitude={latitude}
              longitude={longitude}
            />
            <ExtraInfo
              data={{
                ...data,
                like: getCurrentLikeState(data.id), // Use the optimistic state
              }}
              onOpenCall={handleOpenCall}
              onToggleLike={handleOptimisticToggleLike}
              onShare={() => shareURL(window.location.href)}
              onNavigate={() => setNavigationTarget(data)}
            />
            <HorizontalBar />
            <InfoMenu
              pubType={data.pubType}
              currentMenu={infoMenu}
              onMenuClick={handleClickMenu}
            />
            {infoMenu === "location" && (
              <LocationInfo boxRef={menuListRef[3]} />
            )}
            {infoMenu === "review" && (
              <Review
                boxRef={menuListRef[4]}
                setEditReviewData={setEditReviewData}
                setEditReview={setShowEdit}
                openWrite={() => {
                  setShowWrite(true);
                }}
                closeWrite={() => {
                  setShowWrite(false);
                }}
                reviewData={data.reviewData}
                pubId={data.id}
                todayReviewPhotoAdd={data.todayReviewPhotoAdd}
              />
            )}
            {infoMenu === "restaurant" && (
              <PubRestaurant data={data.restaurant} boxRef={menuListRef[5]} />
            )}
            <HorizontalBar />
            <ReportInfo />
          </div>
        )}
      </StoreDetailWrapper>
      {isOpenUseApp && (
        <OpenApp
          openApp={() => goToApp()}
          onClose={() => setIsOpenUseApp(false)}
        />
      )}
    </StoreContext.Provider>
  );
};

export default StoreDetail;
